import { useEffect, useRef } from "react";
import { playSfx } from "../sound/sfx";

/**
 * Game-layer sound cues, shared by the solo (`game-store`, `store`) and online
 * (`online-store`) game stores so the win/lose and
 * "your turn" decisions live in ONE place (no per-store drift). Each helper
 * delegates to {@link playSfx}, which is already a no-op when the player has muted
 * sound — callers never re-check the toggle.
 */

/**
 * Play THIS player's end-of-match chime. `winners` is the engine's AUTHORITATIVE
 * winner list (`ScoreResult.winners`) — for team games (Hokm, Shelem) it already
 * contains every seat of the winning team — so membership, never a score
 * comparison, decides win vs. lose. "Winning" direction differs per game (Hearts =
 * lowest score, Shelem/Hokm = highest team), so we MUST rely on this canonical
 * list rather than infer it. No-ops when our seat is unknown (nothing to compare).
 */
export function playOutcomeSfx(
  winners: readonly number[],
  mySeat: number | null | undefined,
): void {
  if (mySeat == null) return;
  playSfx(winners.includes(mySeat) ? "win" : "lose");
}

/**
 * Play the win/lose chime WHEN THE RESULT IS REVEALED, not when the terminal
 * frame lands. The stores used to fire it the instant the game ended, but the
 * summary overlay is deliberately held for a beat (`useEndReveal`, 1.4s) so the
 * final trick can finish sweeping — so the chime arrived ~1.4s BEFORE the
 * visual it belongs to. Fires exactly once per revealed result.
 */
export function useOutcomeSfx(
  revealed: boolean,
  winners: readonly number[] | undefined,
  mySeat: number | null | undefined,
): void {
  const played = useRef(false);
  useEffect(() => {
    if (!revealed || !winners) {
      if (!revealed) played.current = false; // re-armed for the next game
      return;
    }
    if (played.current) return;
    played.current = true;
    playOutcomeSfx(winners, mySeat);
  }, [revealed, winners, mySeat]);
}

/*
 * THE "YOUR TURN" BEEP IS GONE (deliberately — the user found it unpleasant, and it
 * also fired at game start, where a beep has nothing to do with dealing cards).
 * `turn.mp3` and its cue helpers are deleted, not merely unwired, so nothing can
 * quietly re-introduce them.
 *
 * What tells you it is your turn now: the felt's countdown ring (online) and your
 * hand lighting up its legal cards. What you hear when a game STARTS is the card
 * SHUFFLE (`useDealSfx` — the deal), which is the sound of the thing that is
 * actually happening.
 */
